import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const EnergyCost = () => {
   // State variables for Energy Cost calculation
   const [powerUsage, setPowerUsage] = useState(0); // in watts
   const [time, setTime] = useState(0); // in hours
   const [energyRate, setEnergyRate] = useState(0); // in ₹ per kWh
   const [energyCost, setEnergyCost] = useState(0);

   // Event handlers to update state values
   const handlePowerUsageChange = (e) => setPowerUsage(parseFloat(e.target.value) || 0);
   const handleTimeChange = (e) => setTime(parseFloat(e.target.value) || 0);
   const handleEnergyRateChange = (e) => setEnergyRate(parseFloat(e.target.value) || 0);

   // useEffect hook to calculate the Energy Cost when inputs change
   useEffect(() => {
     if (powerUsage && time && energyRate) {
       const calculatedEnergyCost = (powerUsage * time) / 1000 * energyRate;
       setEnergyCost(calculatedEnergyCost);
     } else {
       setEnergyCost(0);
     }

     // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

   }, [powerUsage, time, energyRate]);

  return (
    <Layout title="Energy Cost Calculator" path="energy-cost">
      <section className="section-padding" id="energy-cost-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Energy Cost Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Energy Cost Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Power Usage Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="powerUsage" className="form-label">
                        Power Usage (W)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Power Usage:</strong> The amount of power an appliance uses, measured in watts (W)."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="powerUsage"
                        placeholder="Enter appliance power usage in watts"
                        value={powerUsage || ""}
                        onChange={handlePowerUsageChange}
                      />
                    </div>

                    {/* Time Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="time" className="form-label">
                        Usage Time (hours)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Usage Time:</strong> The total number of hours the appliance is used in a day."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="time"
                        placeholder="Enter usage time in hours"
                        value={time || ""}
                        onChange={handleTimeChange}
                      />
                    </div>

                    {/* Energy Rate Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="energyRate" className="form-label">
                        Energy Rate (₹ per kWh)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Energy Rate:</strong> The cost of 1 kilowatt-hour (kWh) of electricity in ₹."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="energyRate"
                        placeholder="Enter energy rate in ₹ per kWh"
                        value={energyRate || ""}
                        onChange={handleEnergyRateChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Energy Cost:{" "}
                        <span className="fw-bold ms-3">
                          {energyCost === 0 ? "0.00" : energyCost.toFixed(2)} ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Energy Cost Calculator
                </h3>

                {/* What is an Energy Cost Calculator? */}
                <h4 className="mt-3 mb-3">What is an Energy Cost Calculator?</h4>
                <p>
                  An **Energy Cost Calculator** helps you estimate the energy expenses of operating an appliance based on its power consumption, how long it is used, and the local energy rate. By calculating the energy cost, you can understand your energy expenses and plan accordingly.
                </p>

                {/* Why Use an Energy Cost Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Energy Cost Calculator?</h4>
                <p>
                  This calculator is essential for individuals and businesses to track and manage energy costs. It helps identify high-energy-consuming appliances and make informed decisions about energy-efficient usage. By calculating the energy cost, users can optimize energy consumption and reduce unnecessary expenses.
                </p>

                {/* Benefits of Using an Energy Cost Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using an Energy Cost Calculator</h4>
                <ul>
                  <li><strong>Supports Cost-Saving Through Energy-Efficient Usage:</strong> Helps identify and manage high-energy-consuming appliances to reduce energy bills.</li>
                  <li><strong>Useful for Household and Business Energy Budgeting:</strong> Allows you to estimate energy expenses and plan your energy usage accordingly.</li>
                  <li><strong>Helps Identify High-Energy-Consuming Appliances:</strong> Identify appliances that consume a lot of energy, enabling you to replace or use them more efficiently.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use an Energy Cost Calculator?</h4>
                <p>
                  The Energy Cost Calculator is ideal for anyone who wants to track and manage their energy consumption, including:
                </p>
                <ul>
                  <li><strong>Households:</strong> Helps families estimate electricity costs and find ways to save on their energy bills.</li>
                  <li><strong>Commercial Buildings:</strong> Useful for businesses looking to monitor energy expenses and improve energy efficiency.</li>
                  <li><strong>Energy-Conscious Industries:</strong> Beneficial for industries and sectors focused on reducing energy consumption and promoting sustainability.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How do I calculate energy costs?</h4>
                <p>
                  The formula for energy cost is: <strong>Energy Cost = (Power Usage × Time) × Energy Rate</strong>. This gives you the total cost for running an appliance for a certain duration at a specified energy rate.
                </p>

                <h4 className="mt-4 mb-3">Why is it important to track energy costs?</h4>
                <p>
                  Tracking energy costs helps in managing your energy consumption more efficiently. By identifying high-energy-consuming appliances, you can either replace them with more energy-efficient options or adjust usage to save on electricity costs.
                </p>

                <h4 className="mt-4 mb-3">How can I save on energy costs?</h4>
                <p>
                  You can save on energy costs by using energy-efficient appliances, adjusting usage times, and reducing the overall energy consumption in your home or business. The energy cost calculator can help identify where savings can be made.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EnergyCost;
